import { Mail, Rows3 } from "lucide-react";
import { NavItem } from "@onemileauto/ui";
import type { MailboxMembership } from "@onemileauto/shared-types";

interface MailboxSwitcherProps {
  mailboxes: MailboxMembership[];
  currentMailboxId: string | undefined;
  isAggregate: boolean;
  onSelect: (mailboxIdOrAll: string) => void;
  className?: string;
}

/**
 * Mailbox switcher (Phase 5: staff mailbox delegation). Deliberately
 * renders nothing for the common case (one mailbox — nothing to switch
 * between) rather than showing a single disabled/pointless option — same
 * reasoning for why "All Inboxes" only appears once there's actually more
 * than one mailbox to aggregate.
 *
 * Same interaction model as FolderNav on purpose (a vertical NavItem list,
 * big touch targets, no dropdown-on-hover/keyboard shortcut) — a second
 * mailbox is just another thing to switch to, not a distinct UI pattern to
 * learn, per this app's non-technical-user constraints.
 */
export function MailboxSwitcher({ mailboxes, currentMailboxId, isAggregate, onSelect, className }: MailboxSwitcherProps) {
  if (mailboxes.length < 2) return null;

  return (
    <nav aria-label="Mailboxes" className={className}>
      <p className="px-3 pb-1 pt-1 text-xs font-medium uppercase tracking-wide text-muted">Mailboxes</p>
      <ul className="flex flex-col gap-0.5">
        <li>
          <NavItem icon={Rows3} label="All Inboxes" active={isAggregate} onClick={() => onSelect("all")} />
        </li>
        {mailboxes.map((mailbox) => (
          <li key={mailbox.id}>
            <NavItem
              icon={Mail}
              label={mailbox.displayName}
              active={!isAggregate && mailbox.id === currentMailboxId}
              onClick={() => onSelect(mailbox.id)}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}
